import type { DeviceSlaAssignment } from '../types';

const parseDateOnlyParts = (value?: string | Date | null) => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth(),
      day: value.getDate()
    };
  }
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const dateOnly = trimmed.slice(0, 10);
  const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) return null;
  return { year, month: monthIndex, day };
};

const parseDateValue = (value?: string | Date | null) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toStartOfDay = (value?: string | Date | null) => {
  const parts = parseDateOnlyParts(value);
  if (!parts) return parseDateValue(value);
  return new Date(parts.year, parts.month, parts.day, 0, 0, 0, 0);
};

const toEndOfDay = (value?: string | Date | null) => {
  const parts = parseDateOnlyParts(value);
  if (!parts) return parseDateValue(value);
  return new Date(parts.year, parts.month, parts.day, 23, 59, 59, 999);
};

const addSlaDurationToDate = (
  startDateInput?: string | Date | null,
  quantityInput?: number | null,
  unitInput?: string | null
) => {
  const startDate = parseDateValue(startDateInput);
  const quantity = Number(quantityInput || 0);
  const unit = String(unitInput || '').trim().toLowerCase();
  if (!startDate || !quantity || !unit) return null;
  const next = new Date(startDate.getTime());
  if (unit === 'hour' || unit === 'hours') {
    next.setHours(next.getHours() + quantity);
    return next;
  }
  if (unit === 'day' || unit === 'days') {
    next.setDate(next.getDate() + quantity);
    return next;
  }
  if (unit === 'week' || unit === 'weeks') {
    next.setDate(next.getDate() + quantity * 7);
    return next;
  }
  if (unit === 'month' || unit === 'months') {
    next.setMonth(next.getMonth() + quantity);
    return next;
  }
  if (unit === 'year' || unit === 'years') {
    next.setFullYear(next.getFullYear() + quantity);
    return next;
  }
  return null;
};

export const resolveDeviceSlaEndDate = (assignment?: DeviceSlaAssignment | null) => {
  if (!assignment) return null;
  const explicitEndDate = toEndOfDay(assignment.endDate);
  if (explicitEndDate) return explicitEndDate;
  return addSlaDurationToDate(
    assignment.startDate,
    assignment.slaTemplate?.durationQuantity ?? null,
    assignment.slaTemplate?.durationPeriodUnit ?? null
  );
};

const getAssignmentSortTime = (assignment?: DeviceSlaAssignment | null) => {
  return (
    parseDateValue(assignment?.startDate)?.getTime() ||
    parseDateValue(assignment?.assignedAt)?.getTime() ||
    0
  );
};

export const getCurrentDeviceSlaAssignment = (
  assignments?: DeviceSlaAssignment[] | null,
  referenceDateInput?: string | Date | null
) => {
  const referenceDate = parseDateValue(referenceDateInput) || new Date();
  const sortedAssignments = Array.isArray(assignments)
    ? [...assignments]
        .filter(Boolean)
        .sort((left, right) => getAssignmentSortTime(right) - getAssignmentSortTime(left))
    : [];

  return (
    sortedAssignments.find(assignment => {
      if (!assignment || assignment.endedAt) return false;
      const templateStatus = String(assignment?.slaTemplate?.status || '').trim().toUpperCase();
      if (templateStatus && templateStatus !== 'ACTIVE') return false;
      const startDate = toStartOfDay(assignment.startDate);
      if (!startDate) return false;
      if (referenceDate.getTime() < startDate.getTime()) return false;
      const endDate = resolveDeviceSlaEndDate(assignment);
      if (endDate && referenceDate.getTime() > endDate.getTime()) return false;
      return true;
    }) || null
  );
};

export const hasSavedDeviceSlaReport = (assignment?: DeviceSlaAssignment | null) => {
  if (!assignment?.slaTemplate) return false;
  return Boolean(
    assignment.slaTemplate.hasSavedReport ||
      assignment.slaTemplate.savedReportAt ||
      assignment.slaTemplate.customerReportSavedAt
  );
};

export const getDeviceSlaLabel = (assignment?: DeviceSlaAssignment | null) => {
  const template = assignment?.slaTemplate;
  if (!template) return 'SLA';
  const brand = String(template.brandingName || template.name || '').trim();
  const tier = String(template.tierBrandingName || '').trim();
  const slaType = String(template.slaType || '').trim();
  const parts = [brand, tier, slaType].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : 'SLA';
};

export const getDeviceSlaPeriodLabel = (assignment?: DeviceSlaAssignment | null) => {
  const quantity = Number(assignment?.slaTemplate?.durationQuantity || 0);
  const unit = String(assignment?.slaTemplate?.durationPeriodUnit || '').trim();
  if (!quantity || !unit) return 'Not recorded';
  return `${quantity} ${unit}`;
};
